import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, bildUrl } from "../api";
import { datumAusSpalten, datumKurz, jahrKurz } from "@stammbaum/shared";
import {
  PersonFormular,
  werteFuerApi,
  LEERE_PERSON,
  type PersonWerte,
} from "../komponenten/PersonFormular";
import { usePersonKarte } from "../komponenten/PersonKarte";
import { useTitel } from "../titel";

/**
 * Personensuche. Ein Eingabefeld, sofortige Treffer, mit der Tastatur
 * bedienbar — mehr braucht das nicht.
 *
 * In das eine Feld tippt man so viel, wie man weiß: "Huber" bringt alle
 * zwanzig, "Huber Hans 1887" genau einen. Keine Operatoren, keine
 * Feldauswahl — die Zerlegung macht `begriffeZerlegen` in @stammbaum/shared.
 *
 * Neue Personen legt man normalerweise von einer bestehenden aus an
 * ("+ Kind", "+ Elternteil"), damit die Verwandtschaft gleich stimmt.
 * Hier geht nur die freistehende Person, für einen neuen Zweig.
 */
export default function Suche() {
  const [text, setText] = useState("");
  const [neu, setNeu] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /*
   * Die Infokarte. Hier gibt es keine Wurzel, also auch keine
   * Verwandtschaftsbezeichnung und kein Umfeld - die Trefferliste kennt
   * nur die Person selbst. Was die Karte dann mehr zeigt als die Zeile:
   * das genaue Datum statt des Jahres, das Alter und die Kurzinfo.
   */
  const karte = usePersonKarte();
  const navigate = useNavigate();
  const client = useQueryClient();

  const { data, isFetching } = useQuery({
    queryKey: ["personen", text],
    queryFn: () => api.personen(text),
  });

  const { data: ich } = useQuery({ queryKey: ["ich"], queryFn: api.ich });

  // Der Suchbegriff im Titel: im Verlauf steht dann „Huber · Stammbaum"
  // statt dreimal derselben Zeile.
  useTitel(text.trim() ? `Suche: ${text.trim()}` : "Suche");
  const darfAnlegen = ich?.rolle !== "betrachter";

  async function anlegen(w: PersonWerte) {
    setFehler(null);
    setLaeuft(true);
    try {
      const person = await api.personAnlegen(werteFuerApi(w));
      client.invalidateQueries({ queryKey: ["personen"] });
      navigate(`/person/${person.id}`);
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Anlegen fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  if (neu) {
    return (
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Neue Person</h1>
        <p className="mb-5 mt-1 text-sm text-black/50 dark:text-white/50">
          Ohne Verwandtschaft. Die Verbindungen legst du danach auf der
          Personenseite an.
        </p>
        <PersonFormular
          start={LEERE_PERSON}
          knopftext="Person anlegen"
          fehler={fehler}
          laeuft={laeuft}
          onSpeichern={anlegen}
          onAbbrechen={() => setNeu(false)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-end gap-3">
        <label className="block flex-1">
          <span className="text-sm text-black/60 dark:text-white/60">Name suchen</span>
          <input
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="z.B. Huber — oder Huber Hans 1887"
            className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-base
                       outline-none focus:border-black/40 dark:border-white/15 dark:bg-neutral-800"
          />
          <p className="mt-1 text-xs text-black/45 dark:text-white/45">
            Mehrere Teile möglich — Familienname, Vorname, Geburtsname, Jahr.
            Es muss alles zutreffen, die Reihenfolge ist egal.
          </p>
        </label>
        {darfAnlegen && (
          <button
            onClick={() => {
              setFehler(null);
              setNeu(true);
            }}
            className="rounded-lg border border-black/15 px-3 py-2 text-sm
                       hover:border-black/40 dark:border-white/15"
          >
            Neue Person
          </button>
        )}
      </div>

      <ul className="mt-6 divide-y divide-black/5 dark:divide-white/10">
        {data?.treffer.map((p) => (
          <li key={p.id}>
            <Link
              to={`/person/${p.id}`}
              {...karte.fuer(p)}
              className="flex items-center gap-3 py-2 hover:bg-black/[0.03] dark:hover:bg-white/5"
            >
              <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-black/10">
                {p.portrait && (
                  <img
                    src={bildUrl(p.portrait.medienId, 64, p.portrait.tagId)}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                )}
              </span>
              <span className="flex-1">
                <span className="font-medium">
                  {p.famname} {p.vorname}
                </span>
                {p.rufname && (
                  <span className="text-black/50 dark:text-white/50"> genannt {p.rufname}</span>
                )}
                {p.maedchenname && (
                  <span className="text-black/50 dark:text-white/50"> geb. {p.maedchenname}</span>
                )}
                {/*
                  Ohne diese Zeile sucht man „Mayr" und bekommt eine Liste
                  lauter Maiers, ohne erkennbaren Grund — genau die
                  Ratlosigkeit, die früher der Rufname ausgelöst hat.
                */}
                {p.gefundenUeber && (
                  <span className="ml-2 text-xs text-black/45 dark:text-white/45">
                    gefunden über {p.gefundenUeber}
                  </span>
                )}
              </span>
              {/*
                Das Geburtsdatum unterscheidet die zwanzig Hubers, das
                Sterbejahr genügt zur Einordnung. Beides über datumKurz,
                damit ein gekürzter Datensatz als Jahr erscheint und nicht
                als erfundener 1. Jänner.
              */}
              <span className="whitespace-nowrap text-sm tabular-nums text-black/50 dark:text-white/50">
                {datumKurz(datumAusSpalten(p, "geburt"))}
                {jahrKurz(datumAusSpalten(p, "tod"))
                  ? `–${jahrKurz(datumAusSpalten(p, "tod"))}`
                  : ""}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {karte.karte}

      {isFetching && <p className="mt-4 text-sm text-black/40">sucht …</p>}
      {data && data.treffer.length === 0 && (
        <p className="mt-6 text-black/50">Keine Person gefunden.</p>
      )}
    </div>
  );
}
